// Styles
import styled from 'styled-components/macro'

// Core
import React, {ChangeEventHandler, Fragment, MouseEventHandler, useEffect, useState} from 'react'
import {useSelector} from 'react-redux'

// Components, services, etc
import {openModal} from 'actions/modalActions'
import {createOrderFromModal} from 'actions/ordersActions'
import {getWallet, saveCard} from 'actions/walletActions'
import StripeIcon from 'assets/stripe-icon.svg'
import Button from 'components/Button'
import Checkbox from 'components/Checkbox'
import Modal from 'components/Modal'
import TextInput from 'components/TextInput'
import Tooltip from 'components/Tooltip'
import {
  CONTACT_SUPPORT_MODAL,
  DELETE_CARD_CONFIRMATION_MODAL,
  UNSAVED_WORK_MODAL
} from 'constants/modalsConstants'
import {CHARGE_AND_SAVE, CHARGE_ONLY, EDIT, PAY, SAVE_ONLY} from 'constants/paymentMethodConstants'
import {productDataDictionary} from 'constants/productCardConstants'
import {
  requiredEmailValidationRules,
  requiredValidationRules,
  stripeValidationRules
} from 'constants/validationRulesConstants'
import {creditsSelector} from 'selectors/creditsSelectors'
import {userSelector} from 'selectors/userSelectors'
import {walletSelector} from 'selectors/walletSelectors'
import {RuleType, validateForm} from 'services/formValidationHelpers'
import type {
  CreateOrderRequestBody,
  CreatePaymentIntentRequestBody,
  OpsOrderType,
  WAlletType
} from 'types/orderTypes'
import CardSection from './CardSection'

// 3rd-party
import {CardNumberElement, useElements, useStripe} from '@stripe/react-stripe-js'
import {Field, FieldInputProps, Form, FormProps} from 'react-final-form'
import {MixpanelEventData} from 'services/mixpanel'
import {useAppDispatch} from 'services/store'

interface CommonProps {
  handleClose: () => void
  handleExited: () => void
  open: boolean
}

interface EditModeProps extends CommonProps {
  mode: 'EDIT'
}
interface PayModeProps extends CommonProps {
  mode: 'PAY'
  orderPayload: CreateOrderRequestBody
  mixpanelEventData: MixpanelEventData
  handleOrderError: (error?: any) => void
  handleOrder: (order: OpsOrderType) => void
  orderPrice?: number
}

type PaymentMethodModalProps = EditModeProps | PayModeProps

type PaymentMethodModalFormContentProps = {
  handleCheckboxChange: (
    a: FieldInputProps<any, HTMLElement>
  ) => ChangeEventHandler<HTMLInputElement>
  handleDeleteCard: MouseEventHandler<HTMLButtonElement>
  handleInputChange: (a: FieldInputProps<any, HTMLElement>) => ChangeEventHandler<HTMLInputElement>
  handleSubmit: () => any
  mode: PaymentMethodModalProps['mode']
  wallet: WAlletType
}

const TOOLTIP_CONTENT =
  'Credit card is safely stored with Stripe and will allow you to conveniently place orders without re-entering card information each time.'

const fieldRulesDictionary: {
  [key: string]: RuleType[]
} = {
  name: requiredValidationRules,
  email: requiredEmailValidationRules,
  cardNumber: stripeValidationRules,
  cardExpiry: stripeValidationRules,
  cardCvc: stripeValidationRules
}

type FormValuesType = {
  name?: string
  email?: string
  cardNumber?: string
  saveCardDetails?: boolean
}
const PaymentMethodModal = ({
  open,
  handleClose,
  handleExited,
  ...restProps
}: PaymentMethodModalProps) => {
  const dispatch = useAppDispatch()
  const wallet = useSelector(walletSelector)
  const {mode} = restProps
  const isEditMode = mode === EDIT
  const {stripeCustomerHasSavedPaymentMethod} = wallet
  const user = useSelector(userSelector)
  const {creditsRemaining} = useSelector(creditsSelector)
  const [initialFormValues, setInitialFormValues] = useState<FormValuesType>()
  const [submitting, setSubmitting] = useState(false)
  const stripe = useStripe()!
  const elements = useElements()
  const productPrice = isEditMode
    ? undefined
    : restProps.orderPrice || productDataDictionary[restProps.orderPayload.productType]?.price
  useEffect(() => {
    if (mode === EDIT) {
      if (stripeCustomerHasSavedPaymentMethod) {
        const {email, last4, nameOnCard} = wallet
        setInitialFormValues({
          name: nameOnCard,
          email,
          cardNumber: `${last4}`
        })
      } else {
        // clear the form if they just deleted their card
        setInitialFormValues({
          name: undefined,
          email: undefined
        })
      }
    }
  }, [mode, stripeCustomerHasSavedPaymentMethod, wallet, user])

  const closeModal = () => {
    handleClose()
  }

  const onIgnoreUnsavedWork = (closeUnsavedWorkModal?: () => void) => () => {
    closeUnsavedWorkModal && closeUnsavedWorkModal()
    closeModal()
  }

  const handleCloseModalRequest = (formIsDirty: boolean) => () => {
    if (formIsDirty) {
      dispatch(
        openModal({
          modalType: UNSAVED_WORK_MODAL,
          onConfirm: onIgnoreUnsavedWork
        })
      )
    } else {
      closeModal()
    }
  }

  const handleContactSupportClick = () => {
    dispatch(
      openModal({
        modalType: CONTACT_SUPPORT_MODAL,
        mixpanelEventData: {
          initiatedFrom: 'Payment Method Modal'
        }
      })
    )
  }

  const handlePaySubmit = async (values: FormValuesType) => {
    if (isEditMode) {
      return
    }

    const {orderPayload, handleOrder, handleOrderError, mixpanelEventData, orderPrice} = restProps
    const {saveCardDetails, name, email} = values

    const paymentPayload: CreatePaymentIntentRequestBody = {
      cardAction: saveCardDetails ? CHARGE_AND_SAVE : CHARGE_ONLY,
      userName: name,
      userPhone: user.phone,
      userEmail: email,
      ...(orderPrice && {paymentAmount: Math.round(orderPrice * 100)})
    }

    const paymentData = {
      paymentPayload,
      stripe,
      cardElement: elements?.getElement(CardNumberElement)!
    }

    // this handles when the payment clears on Stripe's end and communicates back to the
    // UI via the web socket
    const onOrderWebSocketMessage = (order: OpsOrderType) => {
      handleOrder(order)
      closeModal()
      setSubmitting(false)
    }

    const onError: PayModeProps['handleOrderError'] = err => {
      handleOrderError(err)
      closeModal()
      setSubmitting(false)
    }

    return dispatch(
      createOrderFromModal({
        orderPayload,
        paymentData,
        onOrderWebSocketMessage,
        onError,
        mixpanelEventData
      })
    )
  }

  const handleSavePaymentMethodSubmit = (values: FormValuesType) => {
    const {name, email} = values
    const walletPayload: CreatePaymentIntentRequestBody = {
      cardAction: SAVE_ONLY,
      userName: name,
      userEmail: email
    }

    // what will happen when cardElement or stripe is null ?
    const cardElement = elements?.getElement(CardNumberElement)!
    return saveCard(walletPayload, cardElement, stripe!)
      .then(() => {
        closeModal()
        dispatch(getWallet())
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  const handleFormSubmit: FormProps['onSubmit'] = (values, form) => {
    const {dirty} = form.getState()
    if (dirty && stripe && elements) {
      setSubmitting(true)
      if (mode === PAY) {
        return handlePaySubmit(values)
      } else {
        return handleSavePaymentMethodSubmit(values)
      }
    } else {
      closeModal()
    }
  }

  const handleDeleteCard = () => {
    dispatch(openModal({modalType: DELETE_CARD_CONFIRMATION_MODAL}))
  }

  const handleInputChange: PaymentMethodModalFormContentProps['handleInputChange'] = input => se => {
    input.onChange(se.target.value)
  }

  const handleCheckboxChange: PaymentMethodModalFormContentProps['handleCheckboxChange'] = input => se => {
    input.onChange(se.target.checked)
  }

  const validate: FormProps['validate'] = values => {
    return validateForm(fieldRulesDictionary, values)
  }

  return (
    <Form
      // @ts-ignore no className for Form
      className='user-profile-modal-content'
      initialValues={initialFormValues}
      onSubmit={handleFormSubmit}
      validate={validate}
      render={({handleSubmit, dirty}) => (
        <PaymentMethodModal.Styled
          size='md'
          open={open}
          onExited={handleExited}
          onClose={handleCloseModalRequest(dirty)}
          showCloseButton
        >
          <Modal.Header>
            <div className='modal-title-wrap'>
              <h3>Payment Method</h3>
              <span className='subtitle'>
                <PaymentMethodModal.TitleText mode={mode} creditsRemaining={creditsRemaining} />{' '}
                <Button
                  title='Contact Support'
                  variant='tertiary'
                  onClick={handleContactSupportClick}
                />
                .
              </span>
            </div>
          </Modal.Header>
          <Modal.Content>
            <PaymentMethodModal.FormContent
              handleCheckboxChange={handleCheckboxChange}
              handleDeleteCard={handleDeleteCard}
              handleInputChange={handleInputChange}
              handleSubmit={handleSubmit}
              mode={mode}
              wallet={wallet}
            />
          </Modal.Content>
          <Modal.Footer>
            <div className='left'>
              Powered by <img src={StripeIcon} alt='Stripe Icon' />
            </div>
            {!isEditMode || (isEditMode && !stripeCustomerHasSavedPaymentMethod) ? (
              <Fragment>
                <Button
                  className='red'
                  variant='secondary'
                  title='Cancel'
                  size='large'
                  onClick={handleCloseModalRequest(dirty)}
                />
                <Button
                  variant='primary'
                  disabled={submitting}
                  title={
                    <PaymentMethodModal.SubmitButtonTitle
                      submitting={submitting}
                      mode={mode}
                      productPrice={productPrice}
                    />
                  }
                  type='submit'
                  size='large'
                  onClick={handleSubmit}
                />
              </Fragment>
            ) : null}
          </Modal.Footer>
        </PaymentMethodModal.Styled>
      )}
    />
  )
}

PaymentMethodModal.TitleText = ({
  mode,
  creditsRemaining
}: {
  mode: PaymentMethodModalFormContentProps['mode']
  creditsRemaining?: number
}) => {
  let content
  if (mode === PAY) {
    if (creditsRemaining === -1) {
      content = 'Please add a card to continue ordering.'
    } else {
      content = 'Your trial has ended. Add a credit card to continue ordering.'
    }
  } else {
    content = 'Manage your credit card below.'
  }

  return <React.Fragment>{`${content} To set up invoicing`}</React.Fragment>
}

PaymentMethodModal.SubmitButtonTitle = ({
  mode,
  submitting,
  productPrice
}: {
  mode: PaymentMethodModalFormContentProps['mode']
  submitting: boolean
  productPrice?: number
}) => {
  let content
  if (mode === PAY) {
    if (submitting) {
      content = <span>Placing Order&hellip;</span>
    } else {
      content = <span>Place Order - ${productPrice}</span>
    }
  } else {
    if (submitting) {
      content = <span>Saving Card&hellip;</span>
    } else {
      content = <span>Save Card</span>
    }
  }

  return content
}

PaymentMethodModal.FormContent = ({
  handleCheckboxChange,
  handleDeleteCard,
  handleInputChange,
  handleSubmit,
  mode,
  wallet
}: PaymentMethodModalFormContentProps) => {
  const {stripeCustomerHasSavedPaymentMethod} = wallet
  const isEditMode = mode === EDIT
  return (
    <form onSubmit={handleSubmit} className='form-body'>
      <div className='row'>
        <Field name='email'>
          {({input, meta}) => {
            return (
              <TextInput
                placeholder='Invoice Email Address'
                label='Invoice Email Address'
                value={input.value}
                onChange={handleInputChange(input)}
                error={meta.error && meta.touched}
                helperText={meta.touched && meta.error}
                disabled={isEditMode && stripeCustomerHasSavedPaymentMethod}
              />
            )
          }}
        </Field>
      </div>
      <CardSection mode={mode} wallet={wallet} />
      {isEditMode && stripeCustomerHasSavedPaymentMethod ? (
        <Button className='red' variant='tertiary' title='Remove Card' onClick={handleDeleteCard} />
      ) : null}
      {!isEditMode && (
        <Field name='saveCardDetails'>
          {({input, meta}) => {
            return (
              <Checkbox
                label={
                  <Tooltip text={TOOLTIP_CONTENT}>
                    <span className='pointer'>Save card and enable 1-click ordering</span>
                  </Tooltip>
                }
                checked={Boolean(input.value)}
                onChange={handleCheckboxChange(input)}
              />
            )
          }}
        </Field>
      )}
    </form>
  )
}

PaymentMethodModal.Styled = styled(Modal)`
  .header {
    height: 80px;
    padding-bottom: 0;

    .modal-title-wrap {
      align-self: flex-start;

      h3 {
        margin-bottom: 8px;
      }
      .subtitle {
        display: block;
        font-size: 0.875rem;
        line-height: 1.375rem;
        color: ${({theme}) => theme.colors.grayscale.gray};
      }
    }

    .close-button {
      align-self: flex-start;
      /* account for button padding */
      margin-top: -6px;
    }
  }

  .content {
    &.portal-MuiDialogContent-root {
      padding-top: 22px;

      .form-body {
        min-height: 410px;

        .portal-MuiFormControlLabel-root {
          & * {
            cursor: pointer;
          }
        }

        .row {
          display: flex;
          flex-direction: row;
          justify-content: space-between;

          .portal-MuiFormControl-root {
            padding-bottom: 16px;

            &.error {
              padding-bottom: 20px;
              margin-bottom: 8px;
            }

            .portal-MuiFormHelperText-root {
              margin: 0;
            }
          }

          &.split {
            .portal-MuiFormControl-root {
              max-width: calc(50% - 8px);
            }
          }
        }
      }

      .red {
        &:not(.Mui-disabled) {
          color: ${({theme}) => theme.colors.ruby.base};
        }
      }
    }
  }

  .footer {
    padding: 16px 24px;
    min-height: 73px;
    justify-content: space-between;

    .left {
      align-items: center;
      display: flex;
      margin-right: auto;

      > img {
        margin-left: 4px;
      }
    }
  }
`

export default PaymentMethodModal
